import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  CardContent, 
  Typography, 
  TextField,
  Stack,
  Button
} from '@mui/material';
import { 
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { CenterSelector } from '../../common/components';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';
import { WriteoffLookupGrid } from '../components/grids';

export const WriteoffLookup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const { handleCenterChange } = useCenterSelection();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setIsSearchActive(true);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setIsSearchActive(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  if (!company) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard sx={{ maxWidth: 800 }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="error">
                Missing required parameter: company
              </Typography>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 20
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SearchIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#1e293b',
                    mb: 0.5
                  }}
                >
                  Writeoff Lookup
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ color: '#64748b' }}
                >
                  {company} | {center}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Box sx={{ mb: 3 }}>
              <CenterSelector value={center} onChange={handleCenterChange} />
            </Box>
            {/* Search Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Search Writeoffs
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  placeholder="Enter MRN, patient name, account number, writeoff ID, or case ID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '& fieldset': {
                        borderColor: '#e2e8f0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#3b82f6',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={!searchTerm.trim()}
                  sx={{
                    backgroundColor: '#3b82f6',
                    '&:hover': {
                      backgroundColor: '#2563eb',
                    },
                    fontWeight: 600,
                    minWidth: 120
                  }}
                >
                  Search
                </Button>
                {isSearchActive && (
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClearSearch}
                    sx={{
                      borderColor: '#64748b',
                      color: '#64748b',
                      '&:hover': {
                        borderColor: '#475569',
                        backgroundColor: '#f8fafc'
                      },
                      fontWeight: 600,
                      minWidth: 100
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
              <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                Enter at least 3 characters to search for writeoffs by patient information, account details, or writeoff identifiers
              </Typography>
            </Box>

            {/* Results Section */}
            {isSearchActive && searchTerm && (
              <WriteoffLookupGrid 
                company={company}
                center={center}
                searchTerm={searchTerm}
              />
            )}

            {!isSearchActive && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <SearchIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                  Ready to search
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Enter your search criteria above to find writeoffs
                </Typography>
              </Box>
            )}
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default WriteoffLookup;
